import { useMemo } from 'react';

import type { PrescriptionLogT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { medicationStatusLabel, usePrescriptionLogsQuery } from '@/entities/rx';
import { toDayKeyYYYYMMDD, toTimestampMs } from '@/shared/lib';
import { toTableCellText } from '@/shared/lib/table';
import {
  EmptyState,
  formatTimelineTimeLabel,
  mapToDaySections,
  TimelineViewer,
} from '@/shared/ui/primitives';
import { Badge } from '@/shared/ui/shadcn/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/shadcn/sheet';
import { statusToneClassName } from '@/shared/ui/statusTone';

import { PharmacyDetailsCard } from './PharmacyDetailsCard';

type LogItem = PrescriptionLogT & {
  __key: string;
  __timestampMs: number | null;
  __dayKey: string | null;
  __timeLabel: string;
};

function renderLogItem(item: LogItem) {
  const status = item.Status ?? null;
  const medStatus = item.MedicationStatus ?? null;
  const user = item.User ?? null;
  const additional = item.AdditionalInfo ?? null;
  const medStatusLabel = medStatus !== null ? medicationStatusLabel(medStatus) : null;

  const metaParts = [
    status !== null ? `Status: ${status}` : null,
    user !== null ? `User: ${user}` : null,
    item.WasRefillRequest === true ? 'Refill request' : null,
    item.Ignored === true ? 'Ignored' : null,
  ].filter((part): part is string => part !== null);

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-medium text-foreground">
          {medStatusLabel !== null ? (
            <Badge
              className={`${statusToneClassName(medStatusLabel.tone)} rounded-full`}
              data-testid="log-medication-status-badge"
            >
              {medStatusLabel.label}
            </Badge>
          ) : null}
          <span>{metaParts.join(' · ')}</span>
        </div>
        <div className="shrink-0 text-xs text-muted-foreground">{item.__timeLabel}</div>
      </div>

      {additional !== null ? (
        <div className="text-sm text-muted-foreground">{toTableCellText(additional, '')}</div>
      ) : null}
    </div>
  );
}

export interface PrescriptionLogsSheetProps {
  userId: string;
  prescriptionId: string;
  prescriptionName?: string;
  pharmacyId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PrescriptionLogsSheet({
  userId,
  prescriptionId,
  prescriptionName,
  pharmacyId,
  open,
  onOpenChange,
}: PrescriptionLogsSheetProps) {
  const enabledPrescriptionId = open ? prescriptionId.trim() : '';
  const logsQuery = usePrescriptionLogsQuery(userId, enabledPrescriptionId);

  const sections = useMemo(() => {
    if (!logsQuery.data) {
      return [];
    }
    return mapToDaySections(logsQuery.data, (log, index): LogItem => {
      const raw = log.DateTimeStamp ?? null;
      const ms = toTimestampMs(raw);
      const dayKey = toDayKeyYYYYMMDD(raw);
      return {
        ...log,
        __key: `${index}:${raw ?? ''}:${log.Status ?? ''}:${log.User ?? ''}`,
        __timestampMs: ms,
        __dayKey: dayKey,
        __timeLabel: formatTimelineTimeLabel(ms, raw),
      };
    });
  }, [logsQuery.data]);

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
      }}
    >
      <SheetContent side="right" className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Prescription logs</SheetTitle>
          <SheetDescription>
            {prescriptionName
              ? `For ${prescriptionName}`
              : 'DoseSpot activity for this prescription.'}
          </SheetDescription>
        </SheetHeader>

        <PharmacyDetailsCard pharmacyId={open ? pharmacyId : undefined} />

        <div className="flex-1 overflow-auto px-4 pb-4">
          {logsQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Loading logs…</div>
          ) : logsQuery.isError ? (
            <EmptyState
              title="Could not load logs"
              description={
                logsQuery.error instanceof Error
                  ? logsQuery.error.message
                  : 'An unknown error occurred.'
              }
            />
          ) : (
            <TimelineViewer
              sections={sections}
              getItemKey={(item) => item.__key}
              renderItem={renderLogItem}
              empty={
                <EmptyState
                  title="No logs"
                  description="No DoseSpot log entries were returned for this prescription."
                />
              }
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
